import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import { attachScript } from '../../../common/utils/attach-script';
import safeAreaInsets from 'safe-area-insets';
import styles from './styles.scss';
const scriptApiUrl = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.2';
const apiPromise = new Promise(resolve => {
  // there is a chance that in this site there are several things that are loading the same sdk
  // and they are setting their own fbAsyncInit. And if this one is loading the second time, this function
  // may never get called
  const originalFbAsyncInit = window.fbAsyncInit;
  window.fbAsyncInit = () => {
    if (originalFbAsyncInit) {
      originalFbAsyncInit();
    }
    resolve();
  };
});
let scriptAttached = false;
export default class FacebookExternal extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "saveContainerRef", ref => this.containerRef = ref);
    _defineProperty(this, "saveStretchedContainer", ref => this.stretchedContainerRef = ref);
    _defineProperty(this, "init", () => {
      window.FB.init({
        xfbml: true,
        version: 'v3.2'
      });
    });
    _defineProperty(this, "handleFacebookReady", message => {
      if (message.type === 'video' && message.id === this.getPlayerId()) {
        this.onReady(message.instance);
      }
    });
    _defineProperty(this, "setAllowAttribute", () => {
      if (!this.containerRef) {
        return;
      }
      const iframe = this.containerRef.querySelector('iframe');
      if (!iframe) {
        return;
      }
      if (!iframe.allow.includes('autoplay')) {
        iframe.setAttribute('allow', 'autoplay');
        iframe.setAttribute('src', iframe.src);
      }
    });
    _defineProperty(this, "onReady", player => {
      const {
        mobileMode,
        fillAllSpace,
        onIframeRef
      } = this.props;
      this.player = player;
      this.iframeRef = this.containerRef.querySelector('iframe');
      if (mobileMode || fillAllSpace) {
        this.fitPlayer();
      }
      if (mobileMode) {
        safeAreaInsets.onChange(this.fitPlayer);
      }
      onIframeRef(this.iframeRef);
      this.attachEventsToPlayer();
      this.setState({
        isReady: true
      });
      this.setSettings(this.props);
      this.updateFocusAbility();
    });
    _defineProperty(this, "fitPlayer", insets => {
      const iframe = this.iframeRef;
      if (iframe) {
        const stretchedContainerRect = this.stretchedContainerRef.getBoundingClientRect();
        const rect = this.iframeRef.getBoundingClientRect();
        const {
          fillAllSpace
        } = this.props;
        const {
          height,
          width
        } = fillAllSpace ? stretchedContainerRect : this.props;
        let scale = 1;
        if (rect.height) {
          if (!this.videoSize) {
            this.videoSize = {
              height: rect.height,
              width: rect.width
            };
          }
          if (insets) {
            const {
              left = 0,
              right = 0
            } = insets;
            const videoTagWidth = this.videoSize.width - left - right;
            scale = width / videoTagWidth;
          }
          scale *= height / this.videoSize.height;
          iframe.style.transformOrigin = 'top center';
          iframe.style.transform = "scale(" + scale + ")";
        }
      }
    });
    this.player = null;
    this.containerRef = null;
    this.iframeRef = null;
    this.stretchedContainerRef = null;
    this.videoSize = null;
    this.state = {
      isReady: false
    };
  }
  async componentDidMount() {
    if (!scriptAttached) {
      attachScript(scriptApiUrl);
      scriptAttached = true;
    }
    await apiPromise;
    if (this.containerRef) {
      window.FB.Event.subscribe('xfbml.ready', this.handleFacebookReady);
      window.FB.Event.subscribe('iframeplugin:create', this.setAllowAttribute);
      this.init();
    }
  }
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.src === this.props.src) {
      this.setSettings(nextProps, this.props);
    } else {
      this.setState({
        isReady: false
      });
    }
  }
  componentDidUpdate(prevProps) {
    if (this.isPropsChanged(prevProps, ['src', 'width', 'height'])) {
      this.init();
    }
    if (this.isPropsChanged(prevProps, ['isVideoPauseRequested', 'isVideoPlayRequested'])) {
      this.updateFocusAbility();
    }
  }
  componentWillUnmount() {
    if (window.FB) {
      window.FB.Event.unsubscribe('xfbml.ready', this.handleFacebookReady);
      window.FB.Event.unsubscribe('iframeplugin:create', this.setAllowAttribute);
    }
    if (this.player) {
      this.detachEventsFromPlayer();
    }
  }
  isPropsChanged(prevProps, propNames) {
    const prev = _.pick(prevProps, propNames);
    const current = _.pick(this.props, propNames);
    return !_.isEqual(prev, current);
  }
  updateFocusAbility() {
    if (!this.iframeRef) {
      return;
    }
    const {
      isVideoPauseRequested,
      isVideoPlayRequested,
      isVideoPlaying,
      isVideoPaused
    } = this.props;
    const tabIndex = isVideoPauseRequested || isVideoPlayRequested || isVideoPlaying || isVideoPaused ? 0 : -1;
    this.iframeRef.setAttribute('tabindex', tabIndex);
  }
  getPlayerId() {
    return this.props.src + "-" + this.props.compId;
  }
  attachEventsToPlayer() {
    const {
      onPlay,
      onPause,
      onEnd
    } = this.props;
    this._eventHanlers = {};
    this._eventHanlers.onPlay = this.player.subscribe('startedPlaying', onPlay);
    this._eventHanlers.onPause = this.player.subscribe('paused', onPause);
    this._eventHanlers.onEnd = this.player.subscribe('finishedPlaying', onEnd);
  }
  detachEventsFromPlayer() {
    this._eventHanlers.onPlay.release();
    this._eventHanlers.onPause.release();
    this._eventHanlers.onEnd.release();
    this._eventHanlers = {};
  }
  setSettings(_ref, prevProps) {
    let {
      isVideoPlayRequested,
      isVideoPauseRequested,
      muted
    } = _ref;
    if (prevProps === void 0) {
      prevProps = {};
    }
    if (!this.player || !this.state.isReady) {
      return null;
    }
    if (prevProps.isVideoPlayRequested !== isVideoPlayRequested && isVideoPlayRequested) {
      this.togglePaused(false);
    }
    if (prevProps.isVideoPauseRequested !== isVideoPauseRequested && isVideoPauseRequested) {
      this.togglePaused(true);
    }
    if (prevProps.muted !== muted) {
      this.toggleMute(muted);
    }
  }
  togglePaused(paused) {
    if (paused) {
      this.player.pause();
    } else {
      this.player.play();
    }
  }
  toggleMute(muted) {
    if (muted) {
      this.player.mute();
    } else {
      this.player.unmute();
    }
  }
  render() {
    const {
      src,
      className,
      fillAllSpace
    } = this.props;
    const {
      width,
      height
    } = this.props;
    const props = {
      id: this.getPlayerId(),
      className: classnames('fb-video', styles.iframe, className),
      ref: this.saveContainerRef,
      'data-href': src,
      'data-allowfullscreen': 'true'
    };
    if (!fillAllSpace) {
      props['data-width'] = Math.ceil(width);
      props['data-height'] = Math.ceil(height);
    }
    return /*#__PURE__*/React.createElement("div", {
      ref: this.saveStretchedContainer,
      className: styles.absolute
    }, /*#__PURE__*/React.createElement("div", props));
  }
}
_defineProperty(FacebookExternal, "defaultProps", {
  compId: 'comp'
});
//# sourceMappingURL=facebook.js.map